import React from "react";
import { Box, Typography, Tooltip, Chip } from "@mui/material";
import { MoreVertical, Eye, AlertTriangle, Mail, Building2, Plus } from "lucide-react";

import { Card, CardContent } from "../../components/Card";
import { Avatar } from "../../components/Avatar";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import { useTheme } from "../../theme/useTheme";
import PropTypes from "prop-types";

/**
 * Componente para mostrar información de un miembro del equipo
 * Supports multi-agency consultants with agency badges
 */
export const TeamMemberCard = ({
  member,
  onMemberClick,
  onCreateIncident,
  onAssignAgency,
  showDropdown = false,
  canCreateIncidents = false,
  showAgencyBadges = false,
  isSuperAdmin = false,
}) => {
  const { theme, roleBadges } = useTheme();
  const totalClients =
    typeof member.stats?.totalClients === "number"
      ? member.stats.totalClients
      : 0;

  // Multi-agency support
  const agencies = member.agencies || [];
  const isMultiAgency = agencies.length > 1;
  const inactive = member.status === "inactive";

  const handleCardClick = () => {
    if (onMemberClick) {
      onMemberClick(member);
    }
  };

  const handleCreateIncident = (e) => {
    e.stopPropagation();
    if (onCreateIncident) {
      onCreateIncident(member);
    }
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    if (onMemberClick) {
      onMemberClick(member);
    }
  };

  // Función para obtener estilos del badge según el rol usando el theme
  const getBadgeSx = (role) => {
    const roleKeyMap = {
      "Admin de Agencia": "agency_admin",
      "Consultor": "consultant",
      "Super Admin": "superadmin",
      "Cliente": "client"
    };

    const key = roleKeyMap[role] || "default";
    // Asegurarse de que roleBadges existe (por si acaso no se ha propagado el contexto aún)
    const style = (roleBadges && roleBadges[key]) || (roleBadges && roleBadges.default) || {};

    return {
      backgroundColor: style.backgroundColor,
      color: style.color,
      borderColor: style.borderColor,
      fontSize: "0.75rem",
      fontWeight: 500,
      height: "24px",
      flexShrink: 0,
    };
  };

  const badgeSx = getBadgeSx(member.role);

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[2],
        },
        p: 0,
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 0, "&:only-child": { p: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 0 }}>
          <Avatar name={member.name} src={member.profile_image || undefined} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 0.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: "1rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: inactive ? theme.palette.text.secondary : undefined,
                  }}
                >
                  {member.name}
                </Typography>
                <Badge size="sm" color={inactive ? "muted" : "green"} sx={{ flexShrink: 0 }}>
                  {inactive ? "Inactivo" : "Activo"}
                </Badge>
              </Box>

              {showDropdown ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        minWidth: "auto",
                        width: "24px",
                        height: "24px",
                        p: 0,
                      }}
                    >
                      <MoreVertical size={12} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleViewDetails}>
                      <Eye size={16} style={{ marginRight: "8px" }} />
                      Ver detalles
                    </DropdownMenuItem>
                    {canCreateIncidents && member.role === "Consultor" && (
                      <DropdownMenuItem onClick={handleCreateIncident}>
                        <AlertTriangle
                          size={16}
                          style={{ marginRight: "8px" }}
                        />
                        Crear incidencia
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Badge
                  variant="outline"
                  sx={badgeSx}
                >
                  {member.role === "Admin de Agencia"
                    ? "Administrador"
                    : member.role === "Consultor"
                      ? "Consultor"
                      : member.role}
                </Badge>
              )}
            </Box>

            {member.email && (
              <Box
                component="a"
                href={`mailto:${member.email}`}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: theme.palette.text.secondary,
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  mb: 1,
                  transition: "color 0.2s ease-in-out",
                  "&:hover": {
                    color: theme.palette.primary.main,
                    textDecoration: "underline",
                  },
                  "& svg": {
                    width: 14,
                    height: 14,
                  },
                }}
              >
                <Mail />
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontSize: "inherit" }}
                >
                  {member.email}
                </Typography>
              </Box>
            )}

            {/* Agency badges for multi-agency consultants */}
            {showAgencyBadges && agencies.length > 0 && member.role === "Consultor" && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexWrap: "wrap",
                  mb: 0.5,
                }}
              >
                <Building2 size={12} style={{ color: theme.palette.text.secondary }} />
                {agencies.map((agency) => (
                  <Tooltip key={agency.id} title={agency.name} arrow>
                    <Chip
                      label={agency.name}
                      size="small"
                      sx={{
                        height: "20px",
                        fontSize: "0.7rem",
                        backgroundColor: isMultiAgency
                          ? `${theme.palette.primary.main}15`
                          : theme.palette.action.hover,
                        color: isMultiAgency
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                        border: isMultiAgency
                          ? `1px solid ${theme.palette.primary.main}30`
                          : 'none',
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  </Tooltip>
                ))}
                {/* Assign to agency button for SuperAdmin */}
                {isSuperAdmin && onAssignAgency && (
                  <Tooltip title="Asignar a otra agencia" arrow>
                    <Chip
                      icon={<Plus size={12} />}
                      label=""
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAssignAgency(member);
                      }}
                      sx={{
                        height: "20px",
                        width: "28px",
                        minWidth: "28px",
                        cursor: "pointer",
                        backgroundColor: theme.palette.action.hover,
                        "&:hover": {
                          backgroundColor: theme.palette.action.selected,
                        },
                        "& .MuiChip-icon": {
                          margin: 0,
                        },
                        "& .MuiChip-label": {
                          display: "none",
                        },
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}

            {showDropdown ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {member.role !== "Admin de Agencia" && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.muted }}
                    >
                      {totalClients} clientes
                    </Typography>
                  )}
                </Box>
                <Badge
                  variant="outline"
                  sx={badgeSx}
                >
                  {member.role === "Admin de Agencia"
                    ? "Administrador"
                    : member.role === "Consultor"
                      ? "Consultor"
                      : member.role}
                </Badge>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {member.role !== "Admin de Agencia" && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.muted }}
                  >
                    {totalClients} clientes
                  </Typography>
                )}
              </Box>
            )}

          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

TeamMemberCard.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    status: PropTypes.string,
    profile_image: PropTypes.string,
    stats: PropTypes.shape({
      totalClients: PropTypes.number,
    }),
    agencies: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    isMultiAgency: PropTypes.bool,
  }).isRequired,
  onMemberClick: PropTypes.func,
  onCreateIncident: PropTypes.func,
  onAssignAgency: PropTypes.func,
  showDropdown: PropTypes.bool,
  canCreateIncidents: PropTypes.bool,
  showAgencyBadges: PropTypes.bool,
  isSuperAdmin: PropTypes.bool,
};
